import React from "react";
import { FileText } from "react-feather";
import { KgDatabaseIcon } from "../../atoms/KgDatabaseIcon";
import { KgDataStoryIcon } from "../../atoms/KgDataStoryIcon";
import { KgMetricIcon } from "../../atoms/KgMetricIcon";
import { KgOntologyOrbitIcon } from "../../atoms/KgOntologyOrbitIcon";
import { KgPanelTableIcon } from "../../atoms/KgPanelTableIcon";
import type { KgCatalogSectionId } from "../../../services/kg/kgCatalog.types";

export type KgCatalogIconProps = {
  size?: number;
  className?: string;
};

/** Figma `layers` — data models section. */
export const KgCatalogLayersIcon: React.FC<KgCatalogIconProps> = ({
  size = 16,
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
    className={className}
  >
    <path
      d="M8 1.33398L2 4.66732L8 8.00065L14 4.66732L8 1.33398Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 8L8 11.3333L14 8"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 11.334L8 14.6673L14 11.334"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Figma `folder-kanban` — artifacts section header. */
export const KgCatalogFolderKanbanIcon: React.FC<KgCatalogIconProps> = ({
  size = 16,
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
    className={className}
  >
    <path
      d="M14 12.6673H2V3.33398C2 2.96579 2.29848 2.66732 2.66667 2.66732H6.47181C6.70278 2.66732 6.91841 2.77501 7.05719 2.95865L8 4.33398L8.94281 2.95865C9.08159 2.77501 9.29722 2.66732 9.52819 2.66732H13.3333C13.7015 2.66732 14 2.96579 14 3.33398V12.6673Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 8H14"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 8V12.6673"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 8V12.6673"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const wrap =
  (Icon: React.FC<KgCatalogIconProps>): React.FC<KgCatalogIconProps> =>
  (props) => <Icon {...props} />;

/** Section header icons keyed by catalog section — matches Figma + sidebar. */
export const kgCatalogSectionIcons: Record<
  KgCatalogSectionId,
  React.FC<KgCatalogIconProps>
> = {
  dataSource: wrap(KgDatabaseIcon),
  table: wrap(KgPanelTableIcon),
  dataModel: KgCatalogLayersIcon,
  ontology: wrap(KgOntologyOrbitIcon),
  metric: wrap(KgMetricIcon),
  report: ({ size = 16, className }) => (
    <FileText size={size} className={className} aria-hidden />
  ),
  dataStory: wrap(KgDataStoryIcon),
  artifact: KgCatalogFolderKanbanIcon,
};

export const getKgCatalogSectionIcon = (
  sectionId: KgCatalogSectionId,
): React.FC<KgCatalogIconProps> => kgCatalogSectionIcons[sectionId];
